import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Backdrop, Box, IconButton, Modal, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

const SWIPE_THRESHOLD_PX = 64;
const HORIZONTAL_INTENT_RATIO = 1.25;
const BASE_ZOOM_TOLERANCE = 0.01;
const CAROUSEL_CONTROL_STYLES = {
    backgroundColor: 'rgba(10,11,13,0.82)',
    color: '#FFFFFF',
    border: '1px solid rgba(255,255,255,0.14)',
    boxShadow: '0 8px 28px rgba(0,0,0,0.34)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
    '&:hover': {
        backgroundColor: 'rgba(24,26,30,0.92)',
        borderColor: 'rgba(255,255,255,0.22)',
        boxShadow: '0 10px 32px rgba(0,0,0,0.4)',
    },
    '&:focus-visible': {
        backgroundColor: 'rgba(10,11,13,0.92)',
        outline: '2px solid #5B8DEF',
        outlineOffset: '3px',
    },
};
const CAROUSEL_NAV_BUTTON_STYLES = {
    width: 44,
    height: 44,
    color: '#FFFFFF',
    backgroundColor: 'transparent',
    transition: 'background-color 160ms ease, color 160ms ease',
    '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.12)',
    },
    '&:focus-visible': {
        backgroundColor: 'rgba(255,255,255,0.14)',
        outline: '2px solid #5B8DEF',
        outlineOffset: '-2px',
    },
};

const clampImageIndex = (index, imageCount) =>
    Math.min(Math.max(index, 0), Math.max(imageCount - 1, 0));

/**
 * Reusable full-screen image carousel with zoom, circular navigation, and keyboard controls.
 * The parent owns whether the modal is open; this component owns the active slide while open.
 */
const ImageCarouselModal = ({
    open,
    images = [],
    initialIndex = 0,
    imageLabel = 'Evidence',
    onClose,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const swipeStartRef = useRef(null);
    const zoomScaleRef = useRef(1);
    const wasOpenRef = useRef(false);
    const normalizedInitialIndex = clampImageIndex(initialIndex, images.length);
    const displayedIndex =
        open && !wasOpenRef.current
            ? normalizedInitialIndex
            : clampImageIndex(currentIndex, images.length);

    useEffect(() => {
        const isOpening = open && !wasOpenRef.current;
        wasOpenRef.current = open;

        if (!open) return;

        setCurrentIndex((index) =>
            isOpening ? normalizedInitialIndex : clampImageIndex(index, images.length)
        );
    }, [images.length, normalizedInitialIndex, open]);

    useEffect(() => {
        swipeStartRef.current = null;
        zoomScaleRef.current = 1;
    }, [displayedIndex]);

    const showPreviousImage = () => {
        if (images.length < 2) return;
        setCurrentIndex(
            (index) => (clampImageIndex(index, images.length) - 1 + images.length) % images.length
        );
    };

    const showNextImage = () => {
        if (images.length < 2) return;
        setCurrentIndex((index) => (clampImageIndex(index, images.length) + 1) % images.length);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            showPreviousImage();
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            showNextImage();
        }
    };

    const handleTouchStart = (event) => {
        const isBaseZoom = zoomScaleRef.current <= 1 + BASE_ZOOM_TOLERANCE;
        if (images.length < 2 || event.touches.length !== 1 || !isBaseZoom) {
            swipeStartRef.current = null;
            return;
        }

        const touch = event.touches[0];
        swipeStartRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = (event) => {
        const swipeStart = swipeStartRef.current;
        swipeStartRef.current = null;

        const isBaseZoom = zoomScaleRef.current <= 1 + BASE_ZOOM_TOLERANCE;
        if (!swipeStart || event.changedTouches.length !== 1 || !isBaseZoom) return;

        const touch = event.changedTouches[0];
        const horizontalDistance = touch.clientX - swipeStart.x;
        const verticalDistance = touch.clientY - swipeStart.y;
        const isIntentionalHorizontalSwipe =
            Math.abs(horizontalDistance) >= SWIPE_THRESHOLD_PX &&
            Math.abs(horizontalDistance) > Math.abs(verticalDistance) * HORIZONTAL_INTENT_RATIO;

        if (!isIntentionalHorizontalSwipe) return;

        // Prevent the browser from synthesizing a backdrop click after the swipe finishes.
        event.preventDefault();

        if (horizontalDistance < 0) {
            showNextImage();
        } else {
            showPreviousImage();
        }
    };

    const handleTouchMove = (event) => {
        const isBaseZoom = zoomScaleRef.current <= 1 + BASE_ZOOM_TOLERANCE;
        if (event.touches.length !== 1 || !isBaseZoom) {
            swipeStartRef.current = null;
        }
    };

    const handleTransformed = (...transformArgs) => {
        const transformState = transformArgs[1];
        zoomScaleRef.current = transformState.scale;
    };

    const handleImageClick = (event) => {
        const imageElement = event.currentTarget;
        const imageBounds = imageElement.getBoundingClientRect();
        const { naturalWidth, naturalHeight } = imageElement;

        if (!naturalWidth || !naturalHeight || !imageBounds.width || !imageBounds.height) {
            event.stopPropagation();
            return;
        }

        const containedScale = Math.min(
            imageBounds.width / naturalWidth,
            imageBounds.height / naturalHeight
        );
        const visibleWidth = naturalWidth * containedScale;
        const visibleHeight = naturalHeight * containedScale;
        const visibleLeft = (imageBounds.width - visibleWidth) / 2;
        const visibleTop = (imageBounds.height - visibleHeight) / 2;
        const clickX = event.clientX - imageBounds.left;
        const clickY = event.clientY - imageBounds.top;
        const clickedVisibleImage =
            clickX >= visibleLeft &&
            clickX <= visibleLeft + visibleWidth &&
            clickY >= visibleTop &&
            clickY <= visibleTop + visibleHeight;

        if (clickedVisibleImage) {
            event.stopPropagation();
        }
    };

    const currentImage = images[displayedIndex];
    const normalizedLabel = imageLabel.toLowerCase();

    return (
        <Modal
            open={open && !!currentImage}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 300,
                sx: { backgroundColor: 'rgba(0,0,0,0.9)' },
            }}
            sx={{ zIndex: 1300 }}
        >
            <Box
                sx={{
                    position: 'fixed',
                    inset: 0,
                    outline: 'none',
                }}
                aria-label={`${imageLabel} image carousel`}
                aria-modal="true"
                role="dialog"
                tabIndex={-1}
                onKeyDown={handleKeyDown}
                onTouchStartCapture={handleTouchStart}
                onTouchMoveCapture={handleTouchMove}
                onTouchEndCapture={handleTouchEnd}
                onTouchCancelCapture={() => {
                    swipeStartRef.current = null;
                }}
                onClick={onClose}
            >
                <IconButton
                    onClick={(event) => {
                        event.stopPropagation();
                        onClose();
                    }}
                    aria-label="Close image preview"
                    sx={{
                        position: 'fixed',
                        top: { xs: 16, sm: 24 },
                        right: { xs: 16, sm: 24 },
                        zIndex: 3,
                        width: 44,
                        height: 44,
                        ...CAROUSEL_CONTROL_STYLES,
                    }}
                >
                    <CloseIcon sx={{ fontSize: 23 }} />
                </IconButton>

                {images.length > 1 && (
                    <Box
                        aria-label="Image navigation"
                        sx={{
                            position: 'fixed',
                            bottom: {
                                xs: 'calc(16px + env(safe-area-inset-bottom))',
                                sm: 'calc(24px + env(safe-area-inset-bottom))',
                            },
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.25,
                            p: 0.5,
                            border: '1px solid rgba(255,255,255,0.14)',
                            borderRadius: '999px',
                            backgroundColor: 'rgba(10,11,13,0.82)',
                            boxShadow: '0 8px 28px rgba(0,0,0,0.34)',
                            backdropFilter: 'blur(16px)',
                            WebkitBackdropFilter: 'blur(16px)',
                        }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <IconButton
                            onClick={showPreviousImage}
                            aria-label={`Previous ${normalizedLabel} image`}
                            sx={CAROUSEL_NAV_BUTTON_STYLES}
                        >
                            <ChevronLeftIcon sx={{ fontSize: 28 }} />
                        </IconButton>

                        <Typography
                            aria-live="polite"
                            sx={{
                                minWidth: 58,
                                px: 0.75,
                                color: '#FFFFFF',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                lineHeight: 1,
                                textAlign: 'center',
                                letterSpacing: '0.01em',
                            }}
                        >
                            {displayedIndex + 1} / {images.length}
                        </Typography>

                        <IconButton
                            onClick={showNextImage}
                            aria-label={`Next ${normalizedLabel} image`}
                            sx={CAROUSEL_NAV_BUTTON_STYLES}
                        >
                            <ChevronRightIcon sx={{ fontSize: 28 }} />
                        </IconButton>
                    </Box>
                )}

                {currentImage && (
                    <TransformWrapper
                        key={displayedIndex}
                        doubleClick={{ mode: 'zoomIn', step: 0.7 }}
                        wheel={{ step: 0.2 }}
                        minScale={1}
                        maxScale={6}
                        centerOnInit
                        onTransformed={handleTransformed}
                    >
                        <TransformComponent
                            wrapperStyle={{ width: '100vw', height: '100vh' }}
                            contentStyle={{
                                width: '100vw',
                                height: '100vh',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <img
                                src={currentImage}
                                alt={`${imageLabel} ${displayedIndex + 1} of ${images.length}`}
                                onClick={handleImageClick}
                                style={{
                                    display: 'block',
                                    width: '96vw',
                                    height:
                                        images.length > 1
                                            ? 'calc(100vh - 176px - env(safe-area-inset-bottom))'
                                            : '92vh',
                                    objectFit: 'contain',
                                    pointerEvents: 'auto',
                                    borderRadius: 4,
                                }}
                                onError={(event) => {
                                    event.target.src =
                                        'https://via.placeholder.com/800x600?text=Image+Error';
                                }}
                            />
                        </TransformComponent>
                    </TransformWrapper>
                )}
            </Box>
        </Modal>
    );
};

ImageCarouselModal.propTypes = {
    open: PropTypes.bool.isRequired,
    images: PropTypes.arrayOf(PropTypes.string),
    initialIndex: PropTypes.number,
    imageLabel: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};

export default ImageCarouselModal;
